import { useRef } from 'react'
import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from 'motion/react'
import type { DisciplineStats } from '@/hooks/useDashboardChannel'
import { NameMarquee } from '@/components/dashboard/fx/NameMarquee'
import { SplitText } from '@/components/dashboard/fx/SplitText'
import { EASE } from '@/components/dashboard/fx/motion'

interface HeroProps {
  connected: boolean
  stats: DisciplineStats | undefined
}

const PLACEHOLDER_WORDS = ['Loading', 'the', 'catalog', 'of', 'everything', 'they', 'play']

export function Hero({ connected, stats }: HeroProps) {
  const ref = useRef<HTMLElement>(null)

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
  const textY = useTransform(scrollYProgress, [0, 1], [0, 140])
  const textOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0])

  // A big soft glow that chases the cursor with spring lag.
  const x = useMotionValue(520)
  const y = useMotionValue(260)
  const sx = useSpring(x, { stiffness: 90, damping: 22 })
  const sy = useSpring(y, { stiffness: 90, damping: 22 })
  const glow = useMotionTemplate`radial-gradient(640px circle at ${sx}px ${sy}px, color-mix(in oklab, var(--primary) 26%, transparent), transparent 70%)`

  const artists = stats?.top_artists.map((a) => a.name) ?? PLACEHOLDER_WORDS
  const instructors = stats?.top_instructors.map((i) => i.name) ?? PLACEHOLDER_WORDS

  return (
    <section
      ref={ref}
      onPointerMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect()
        x.set(e.clientX - rect.left)
        y.set(e.clientY - rect.top)
      }}
      className="relative isolate flex min-h-[calc(100svh-3.6rem)] flex-col justify-between gap-16 overflow-hidden pb-10 pt-16 sm:pt-24"
    >
      <div className="aurora" aria-hidden>
        <i />
        <i />
        <i />
      </div>
      <motion.div aria-hidden style={{ background: glow }} className="pointer-events-none absolute inset-0 -z-10" />

      {/* flex-1 + items-center puts the headline in the middle of the space
          above the marquees -- on a tall/wide screen it used to hug the
          top and leave a big empty hole below. */}
      <div className="flex flex-1 items-center">
      <motion.div style={{ y: textY, opacity: textOpacity }} className="mx-auto w-full max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-border bg-background/40 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] backdrop-blur"
        >
          <span className="relative flex size-2">
            {connected && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />}
            <span className={`relative inline-flex size-2 rounded-full ${connected ? 'bg-primary' : 'bg-muted-foreground'}`} />
          </span>
          {connected ? 'Live' : 'Connecting'}
          <span className="hidden sm:inline">· Peloton Playlist Intelligence</span>
        </motion.div>

        <h1 className="font-display text-[clamp(2.75rem,8.2vw,8rem)] font-semibold leading-[0.92] tracking-tighter">
          <span className="block">
            <SplitText text="What they play." />
          </span>
          <span className="block">
            <SplitText text="And what they" delay={0.25} />
            <SplitText text="don't." delay={0.45} className="font-accent font-normal italic tracking-normal text-gradient" />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-lg text-muted-foreground"
        >
          Every song across tens of thousands of classes, tallied and cross-examined -- to find the tracks that
          instructors keep reaching for, and the ones they never touch.
        </motion.p>
      </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.1 }}
        className="flex flex-col gap-3"
      >
        <NameMarquee words={artists} duration={70} />
        <NameMarquee words={instructors} duration={85} reverse />
      </motion.div>
    </section>
  )
}
